import {
  SPACING_ENUM_KEYS,
  type ColorSetTokenMap,
  type CornerTokenMap,
  type ProjectThemeOverrides,
  type SpacingEnumKey,
  type SurfaceTone,
  type ThemeRegistryEntry,
} from './types';
import { resolveProjectColorSet } from './themeResolve';
import { ATMOSPHERE, getThemeSiteComposition, type AtmosphereId } from './themeSiteComposition';

/** Luxury dark-mode baseline — near-black slate; soft off-white ink (not pure white). */
export const LUXURY_DARK_SURFACES = {
  bgBase: 'oklch(0.08 0.005 24)',
  bgSurface: 'oklch(0.14 0.01 24)',
  /** Body ink: warm L≈0.90 — softer than pure white, brighter than L 0.86. */
  textMain: 'oklch(0.90 0.01 24)',
  textMuted: 'oklch(0.74 0.01 24)',
  borderSubtle: 'oklch(0.24 0.01 24)',
} as const;

/** Per-theme dark ground recipes — keep brand accents; vary base/surface hue. */
const ATMOSPHERE_SURFACES: Record<
  Exclude<AtmosphereId, 'none'>,
  Pick<ColorSetTokenMap, 'bgBase' | 'bgSurface'>
> = {
  [ATMOSPHERE.inkDeep]: {
    bgBase: 'oklch(0.09 0.012 55)',
    bgSurface: 'oklch(0.15 0.014 55)',
  },
  [ATMOSPHERE.velvetRoom]: {
    bgBase: 'oklch(0.09 0.03 20)',
    bgSurface: 'oklch(0.15 0.035 20)',
  },
  [ATMOSPHERE.neonVoid]: {
    bgBase: 'oklch(0.07 0.02 290)',
    bgSurface: 'oklch(0.13 0.025 290)',
  },
  [ATMOSPHERE.streetAsphalt]: {
    bgBase: 'oklch(0.1 0.008 250)',
    bgSurface: 'oklch(0.16 0.01 250)',
  },
};

export function applyAtmosphereTokens(
  tokens: ColorSetTokenMap,
  atmosphere: AtmosphereId,
): ColorSetTokenMap {
  if (atmosphere === ATMOSPHERE.none) return tokens;
  /* Light / mid color-sets keep authored grounds (e.g. fine-dining day tones). */
  if (classifySurfaceTone(tokens.bgBase) !== 'dark') return tokens;
  const surfaces = ATMOSPHERE_SURFACES[atmosphere];
  if (!surfaces) return tokens;
  return { ...tokens, ...surfaces };
}

export function oklchLightness(token: string): number | null {
  const match = token.trim().match(/oklch\(\s*([0-9]*\.?[0-9]+)/i);
  if (!match) return null;
  const l = Number.parseFloat(match[1] ?? '');
  return Number.isFinite(l) ? l : null;
}

/**
 * Classify customer site background for preview presentation (studio mat contrast).
 * Dark L < 0.38 · Light L > 0.70 · otherwise mid.
 */
export function classifySurfaceTone(bgBase: string): SurfaceTone {
  const l = oklchLightness(bgBase);
  if (l === null) return 'mid';
  if (l < 0.38) return 'dark';
  if (l > 0.7) return 'light';
  return 'mid';
}

/**
 * Quiet hairline from surface tokens — never alias to `textMuted` (that paints
 * borders as body ink and looks “thick” on dark luxury themes).
 */
export function resolveBorderSubtleCss(tokens: ColorSetTokenMap): string {
  const tone = classifySurfaceTone(tokens.bgBase);
  if (tone === 'dark') {
    /* Same-ink seam: a little main text into surface (matches LUXURY_DARK_SURFACES.borderSubtle intent). */
    return `color-mix(in oklch, ${tokens.textMain} 14%, ${tokens.bgSurface})`;
  }
  /* Light / mid: muted ink into surface — soft rule, not a second text color. */
  return `color-mix(in oklch, ${tokens.textMuted} 22%, ${tokens.bgSurface})`;
}

/**
 * Tone-aware solid CTA interaction deltas for dual light+dark colorsets.
 * Hover lightens on dark grounds and darkens on light/mid; press always darkens.
 * Relative color in site.css: `oklch(from var(--color-brand-primary) calc(l + var(--ws-cta-hover-l)) c h)`.
 */
export function ctaInteractionTokensToCssCustomProperties(
  tokens: ColorSetTokenMap,
): Record<string, string> {
  const tone = classifySurfaceTone(tokens.bgBase);
  const primaryL = oklchLightness(tokens.brandPrimary);
  const onPrimary =
    primaryL !== null && primaryL > 0.65
      ? 'oklch(0.16 0.02 260)'
      : primaryL !== null && primaryL < 0.42
        ? 'oklch(0.98 0.01 95)'
        : tokens.bgBase;

  return {
    '--color-on-primary': onPrimary,
    /* Unitless L deltas for relative-color calc() in site.css */
    '--ws-cta-hover-l': tone === 'dark' ? '0.05' : '-0.05',
    '--ws-cta-press-l': '-0.08',
  };
}

/** CSS custom properties for customer site + configurator preview canvas (registry OKLCH). */
export function colorSetTokensToCssCustomProperties(
  tokens: ColorSetTokenMap,
): Record<string, string> {
  return {
    '--color-brand-primary': tokens.brandPrimary,
    '--color-brand-accent': tokens.brandAccent,
    '--color-bg-base': tokens.bgBase,
    '--color-bg-surface': tokens.bgSurface,
    '--color-text-main': tokens.textMain,
    '--color-text-muted': tokens.textMuted,
    '--color-border-subtle': resolveBorderSubtleCss(tokens),
    ...ctaInteractionTokensToCssCustomProperties(tokens),
  };
}

/**
 * Spacing tokens — `-sm` = phone, bare = md+ (same contract as section/block).
 * Inline gutters must stay large enough for nav/hero chrome; never pin both
 * breakpoints to sub-1rem values (that crushed cafe overlay/full-bleed padding).
 */
const SPACING_CSS_MAP: Record<
  SpacingEnumKey,
  {
    '--ws-space-section-sm': string;
    '--ws-space-section': string;
    '--ws-space-block-sm': string;
    '--ws-space-block': string;
    '--ws-space-inline-sm': string;
    '--ws-space-inline': string;
  }
> = {
  compact: {
    '--ws-space-section-sm': '2rem',
    '--ws-space-section': '2.5rem',
    '--ws-space-block-sm': '1rem',
    '--ws-space-block': '1.25rem',
    '--ws-space-inline-sm': '1.25rem',
    '--ws-space-inline': '2.5rem',
  },
  balanced: {
    '--ws-space-section-sm': '2.5rem',
    '--ws-space-section': '4rem',
    '--ws-space-block-sm': '1.25rem',
    '--ws-space-block': '1.75rem',
    '--ws-space-inline-sm': '1.5rem',
    '--ws-space-inline': '2.75rem',
  },
  relaxed: {
    '--ws-space-section-sm': '3rem',
    '--ws-space-section': '5.5rem',
    '--ws-space-block-sm': '1.5rem',
    '--ws-space-block': '2.25rem',
    '--ws-space-inline-sm': '1.75rem',
    '--ws-space-inline': '3.25rem',
  },
};

export function spacingEnumToCssCustomProperties(
  spacingEnum: SpacingEnumKey,
): Record<string, string> {
  return { ...SPACING_CSS_MAP[spacingEnum] };
}

export function typographyTokensToCssCustomProperties(
  typography: ThemeRegistryEntry['typography_tokens'],
): Record<string, string> {
  const heading = `'${typography.headingFont}', ui-serif, Georgia, serif`;
  const body = `'${typography.bodyFont}', ui-sans-serif, system-ui, sans-serif`;
  return {
    '--ws-font-heading': heading,
    '--ws-font-body': body,
    '--ws-font-brand-heading': heading,
    '--ws-font-brand-body': body,
    '--ws-type-scale': String(typography.scaleFactor),
  };
}

/** Maps corner presets → CSS vars (`sm` fields, `cta` buttons, `md` surfaces, `pill` chips). */
export function cornerTokensToCssCustomProperties(
  corner: CornerTokenMap,
): Record<string, string> {
  return {
    '--ws-radius-sm': corner.sm,
    '--ws-radius-md': corner.md,
    '--ws-radius-pill': corner.pill,
    '--ws-radius-cta': corner.cta,
  };
}

/** Merged registry CSS custom properties for preview canvas + published HTML. */
export function buildProjectThemeCssProperties(
  themeId: string,
  colorsetId?: string,
  spacingEnum: SpacingEnumKey = SPACING_ENUM_KEYS.balanced,
  overrides?: Pick<
    ProjectThemeOverrides,
    'cornerEnum' | 'typographyEnum' | 'customColorSets'
  >,
): Record<string, string> | null {
  const resolved = resolveProjectColorSet(themeId, colorsetId, overrides);
  if (!resolved) return null;
  const atmosphere = getThemeSiteComposition(themeId)?.atmosphere ?? ATMOSPHERE.none;
  const tokens = applyAtmosphereTokens(resolved.tokens, atmosphere);
  return {
    ...colorSetTokensToCssCustomProperties(tokens),
    ...typographyTokensToCssCustomProperties(resolved.typography),
    ...cornerTokensToCssCustomProperties(resolved.corner),
    ...spacingEnumToCssCustomProperties(spacingEnum),
  };
}

/** Inline `style` attribute for published HTML / preview (exact registry values). */
export function buildProjectThemeInlineStyle(
  themeId: string,
  colorsetId?: string,
  spacingEnum: SpacingEnumKey = SPACING_ENUM_KEYS.balanced,
  overrides?: Pick<
    ProjectThemeOverrides,
    'cornerEnum' | 'typographyEnum' | 'customColorSets'
  >,
): string {
  const vars = buildProjectThemeCssProperties(themeId, colorsetId, spacingEnum, overrides);
  if (!vars) return '';
  return Object.entries(vars)
    .map(([key, value]) => `${key}:${value}`)
    .join(';');
}
